/**
 * In-Memory API Caching Mechanism
 *
 * This module provides a simple in-memory cache for API calls. It avoids redundant requests
 * by storing results in a nested Map structure, keyed by API name and a hashed version of the parameters.
 *
 * How it works:
 * - Each API call has a `name` and `params`.
 * - The `params` are sanitized (e.g., user object removed) and then converted to a consistent string key.
 * - If a cached value is found and not expired, it is returned.
 * - Otherwise, the original function is executed and its result cached for future use.
 *
 * Cache Structure:
 * cacheMap = {
 *   "getUserData" => {
 *      "hashedParams1" => {
 *          data: <response>,
 *          addedOn: 1717500000000,
 *          validTill: 1717500300000,
 *          retainForSession: false
 *      },
 *      "hashedParams2" => {...}
 *   },
 *   "getProducts" => {
 *      "hashedParamsA" => {...}
 *   }
 * }
 *
 * Supports:
 * - Custom expiration time per request
 * - Session-persistent cache entries (`retainForSession`)
 * - Resetting cache per API or globally
 */

type Cache<T = any> = {
  data: T,
  addedOn: number,
  validTill: number,
  retainForSession: boolean,
};

export type ApiCaller<T = any> = {
  name: string,
  params: any,
  expirationTime?: number,
  retainForSession?: boolean,
  fn: () => Promise<T>,
};

type ApiCache = Map<string, Cache>;
type ApiCacheMap = Map<string, ApiCache>;

// Top-level cache map
let cacheMap: ApiCacheMap = new Map();
let enableGlobalCache = false;
let defaultExpirationTime = 5 * 60 * 1000; // 5 mins
export function setEnableGlobalCache(exp: any) {
  enableGlobalCache = !!exp;
  if (exp?.variant?.cacheTime) {
    defaultExpirationTime = exp?.variant?.cacheTime * 1000; // cacheTime comes in seconds.
  }
}

/**
 * Check if a cache entry is expired
 * @param validTill - timestamp in ms
 * @returns true if expired, false otherwise
 */
function isCacheExpired(validTill: number): boolean {
  return Date.now() > validTill;
}

/**
 * Recursively removes the `user` key from an object.
 * This prevents cache misses caused by frequent updates in user object (e.g., login timestamp).
 *
 * @param obj - any object or array
 * @returns sanitized object
 */
function removeUserFields(obj: any): any {
  if (Array.isArray(obj)) return obj.map(removeUserFields);
  if (obj && typeof obj === 'object') {
    const newObj: any = {};
    for (const key in obj) {
      if (key === 'user') continue; // skip 'user'
      newObj[key] = removeUserFields(obj[key]);
    }
    return newObj;
  }
  return obj;
}

/**
 * Generates a consistent, order-insensitive JSON string key for the input object.
 * This avoids cache misses due to differently ordered properties.
 *
 * @param obj - parameters object
 * @returns string key
 */
function generateCacheKey(obj: any): string {
  return JSON.stringify(obj, (_, v) =>
    typeof v === 'object' && v !== null
      ? Object.keys(v).sort((a, b) => a.localeCompare(b)).reduce((acc, key) => {
        acc[key] = v[key];
        return acc;
      }, {} as any)
      : v
  );
}

/**
 * Main interceptor function to fetch from cache or make API call.
 *
 * @param request - object with API name, params, expiration, and the function to call
 * @returns cached or freshly fetched API response
 */
export async function cacheInterceptor<T>(request: ApiCaller<T>): Promise<T> {
  const { name, params, expirationTime, retainForSession } = request;

  const sanitizedParams = removeUserFields(params);
  const key = generateCacheKey(sanitizedParams);

  const cacheByNameMap = cacheMap.get(name);
  const cacheByHash = cacheByNameMap?.get(key);

  if (cacheByHash && (cacheByHash.retainForSession || !isCacheExpired(cacheByHash.validTill))) {
    return cacheByHash.data;
  }

  // Delete expred cache
  if (cacheByHash && isCacheExpired(cacheByHash.validTill)) cacheByNameMap.delete(key);

  const data = await request.fn();
  const validTill = Date.now() + (expirationTime ?? defaultExpirationTime); // Default TTL: 5 minutes

  const newCache: Cache<T> = {
    data,
    addedOn: Date.now(),
    validTill,
    retainForSession: !!retainForSession,
  };

  if (cacheByNameMap) {
    cacheByNameMap.set(key, newCache);
  } else {
    cacheMap.set(name, new Map([[key, newCache]]));
  }

  return data;
}

/**
 * Clear cache for a specific API by name
 * @param name - API identifier
 */
export function resetCacheByName(name: string) {
  cacheMap.delete(name);
}

/**
 * Completely resets all cached data
 */
export function resetCache() {
  cacheMap = new Map();
}

/**
 * Call this function to call an api with cache
 * @param name
 * @param fn
 * @param config
 * @returns
 */
export function withCache<T, Y = any>(
  name: string,
  fn: (params: Y) => Promise<T>,
  config?: {
    expirationTime?: number,
    retainForSession?: boolean,
    bypass?: boolean
  }) {
  return (params: Y) =>
    config?.bypass || !enableGlobalCache
      ? fn(params)
      : cacheInterceptor({
        name,
        params,
        fn: () => fn(params),
        ...config,
      });
}
